import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  DollarCircleOutlined,
  EyeOutlined,
  KeyOutlined,
  SearchOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Descriptions,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Space,
  Statistic,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { PERMISSIONS } from "../../../constants/permissions";
import { usePermissions } from "../../../hooks/usePermissions";
import { deviceApi, salesApi, sellerApi } from "../../../services/api";
import { RootState } from "../../../store";
import { ApiPageRequest } from "../../../types/api.types";
import { SellerDeviceListResponse } from "../../../types/device.types";
import {
  SellerInfo,
} from "../../../types/seller.types";
import { formatDate } from "../../../utils/date";

const { Title } = Typography;
const { RangePicker } = DatePicker;

const ManageSellerPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [seller, setSeller] = useState<SellerInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [devices, setDevices] = useState<SellerDeviceListResponse[]>([]);
  const [deviceLoading, setDeviceLoading] = useState(false);
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);
  const [searchText, setSearchText] = useState("");
  const [toggleLoading, setToggleLoading] = useState(false);
  const [dueNotifToggleLoading, setDueNotifToggleLoading] = useState(false);
  const [payAmount, setPayAmount] = useState<string>("");
  const [payLoading, setPayLoading] = useState(false);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<string[]>([]);
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const [unlockLoading, setUnlockLoading] = useState(false);
  const [blockedDevices, setBlockedDevices] = useState(true);
  const [form] = Form.useForm();

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
  });
  
  const { hasPermission } = usePermissions();

  useEffect(() => {
    fetchSellerDetails();
  }, []);

  const fetchSellerDetails = async () => {
    try {
      setLoading(true);
      const state = (location.state as { data?: SellerInfo }) || {};
      if (!state.data) {
        message.error("Invalid seller data");
        navigate(-1);
        return;
      }
      setSeller(state.data);
      if (state.data.id) {
        const fresh = await sellerApi.getById(state.data.id);
        if (fresh.status === "SUCCESS" && fresh.data) {
          setSeller(fresh.data);
        }
      }
    } catch (error) {
      message.error("Failed to fetch seller details");
    } finally {
      setLoading(false);
    }
  };

  const refreshSellerDue = async () => {
    if (!seller?.id) return;
    try {
      const fresh = await sellerApi.getById(seller.id);
      if (fresh.status === "SUCCESS" && fresh.data) {
        setSeller(fresh.data);
      }
    } catch (error) {
      // non-fatal
    }
  };

  const submitPayment = async (amount: number) => {
    if (!seller?.id) return;
    try {
      setPayLoading(true);
      const response = await salesApi.paySellerDues(seller.id, amount);
      if (response.status === "SUCCESS") {
        message.success(response.message || "Payment applied");
        setPayAmount("");
        await refreshSellerDue();
      } else {
        message.error(response.message || "Failed to apply payment");
      }
    } catch (error: any) {
      message.error(error?.message || "Failed to apply payment");
    } finally {
      setPayLoading(false);
    }
  };

  const handlePaySellerDues = () => {
    if (!seller?.id) return;
    const amount = Number(payAmount);
    if (!amount || amount <= 0) {
      message.error("Enter a valid payment amount");
      return;
    }
    const currentDue = seller.totalDue || 0;
    if (currentDue <= 0) {
      message.error("No outstanding dues to pay");
      return;
    }
    if (amount > currentDue) {
      const surplus = amount - currentDue;
      Modal.confirm({
        title: "Amount exceeds outstanding due",
        icon: <WarningOutlined style={{ color: "#faad14" }} />,
        content: (
          <div>
            <p style={{ marginBottom: 8 }}>
              Payment of <strong>BDT {amount.toLocaleString()}</strong>{" "}
              exceeds the outstanding due of{" "}
              <strong>BDT {currentDue.toLocaleString()}</strong>.
            </p>
            <p style={{ marginBottom: 0 }}>
              The surplus of <strong>BDT {surplus.toLocaleString()}</strong>{" "}
              will be applied as advance credit on the most recent sale.
            </p>
          </div>
        ),
        okText: "Pay anyway",
        cancelText: "Cancel",
        okButtonProps: { danger: true },
        onOk: () => submitPayment(amount),
      });
      return;
    }
    submitPayment(amount);
  };

  const fetchDevices = async () => {
    if (!seller || !seller.sellerId) return;

    try {
      setDeviceLoading(true);

      const request: ApiPageRequest<SellerDeviceListResponse> = {
        pageNumber: pagination.current - 1,
        pageSize: pagination.pageSize,
        filter: {
          sellerId: seller.sellerId,
          active: blockedDevices,
        },
      };

      if (dateRange && dateRange[0] && dateRange[1]) {
        request.filter.createdAt = `${dateRange[0]}-${dateRange[1]}`;
      }

      if (searchText) {
        request.filter.imei1 = searchText;
        request.filter.customerPhone = searchText;
      }

      const response = await deviceApi.getDevicesBySeller(
        seller.sellerId,
        request
      );

      if (response.status === "SUCCESS") {
        setDevices(Array.isArray(response.data) ? response.data : []);
        setPagination({
          ...pagination,
          total: response.totalElements || 0,
        });

        // Reset selections when data changes
        setSelectedDeviceIds([]);
        setSelectAllChecked(false);
      } else {
        message.error(response.message || "Failed to fetch devices");
      }
    } catch (error) {
      message.error("Failed to fetch devices");
    } finally {
      setDeviceLoading(false);
    }
  };

  const handleToggleUninstallConfirmation = async (checked: boolean) => {
    try {
      setToggleLoading(true);

      const response = await sellerApi.toggleUninstallConfirmation(
        seller?.id || 0,
        checked
      );

      if (response.status === "SUCCESS") {
        message.success(
          response.message || "Uninstall confirmation requirement updated"
        );
        setSeller((prev) =>
          prev ? { ...prev, uninstallConfirmationRequired: checked } : null
        );
      } else {
        message.error(
          response.message ||
            "Failed to update uninstall confirmation requirement"
        );
      }
    } catch (error) {
      message.error("Failed to update uninstall confirmation requirement");
    } finally {
      setToggleLoading(false);
    }
  };

  const handleToggleDuePaymentNotification = async (checked: boolean) => {
    try {
      setDueNotifToggleLoading(true);

      const response = await sellerApi.toggleDuePaymentNotification(
        seller?.id || 0,
        checked
      );

      if (response.status === "SUCCESS") {
        message.success(
          response.message || "Due payment notification setting updated"
        );
        setSeller((prev) =>
          prev ? { ...prev, duePaymentNotificationEnabled: checked } : null
        );
      } else {
        message.error(
          response.message || "Failed to update due payment notification setting"
        );
      }
    } catch (error) {
      message.error("Failed to update due payment notification setting");
    } finally {
      setDueNotifToggleLoading(false);
    }
  };

  const handleSearch = () => {
    setPagination({
      ...pagination,
      current: 1,
    });
    fetchDevices();
  };

  const handleDateRangeChange = (dates: any, dateStrings: [string, string]) => {
    setDateRange(dateStrings[0] && dateStrings[1] ? dateStrings : null);
  };

  const onSelectChange = (deviceId: string, checked: boolean) => {
    setSelectedDeviceIds((prev) => {
      if (checked) {
        return [...prev, deviceId];
      } else {
        return prev.filter((id) => id !== deviceId);
      }
    });
  };

  const onSelectAllChange = (checked: boolean) => {
    setSelectAllChecked(checked);
    if (checked) {
      setSelectedDeviceIds(devices.map((device) => device?.deviceId || ""));
    } else {
      setSelectedDeviceIds([]);
    }
  };

  const handleUnlockSelectedDevices = async () => {
    if (selectedDeviceIds.length === 0) {
      message.warning("Please select devices to unlock");
      return;
    }

    try {
      setUnlockLoading(true);

      const response = await deviceApi.unlockDevices({
        sellerId: seller?.sellerId || "",
        deviceIds: selectedDeviceIds,
      });

      if (response.status === "SUCCESS") {
        message.success("Devices unlocked successfully");
        fetchDevices(); // Refresh the device list
      } else {
        message.error(response.message || "Failed to unlock devices");
      }
    } catch (error) {
      message.error("Failed to unlock devices");
    } finally {
      setUnlockLoading(false);
    }
  };

  const handleTableChange = (page: number, pageSize: number) => {
    console.log(page, pageSize);
    setPagination({
      ...pagination,
      current: page,
      pageSize: pageSize,
    });
    console.log(pagination);
    // fetchDevices();
  };

  const deviceColumns: ColumnsType<SellerDeviceListResponse> = [
    {
      title: (
        <Checkbox
          checked={selectAllChecked}
          onChange={(e) => onSelectAllChange(e.target.checked)}
        />
      ),
      key: "selection",
      width: 50,
      render: (_, record) => (
        <Checkbox
          checked={selectedDeviceIds.includes(record?.deviceId || "")}
          onChange={(e) => onSelectChange(record?.deviceId || "", e.target.checked)}
        />
      ),
    },
    {
      title: "Device ID",
      dataIndex: "deviceId",
      key: "deviceId",
    },
    {
      title: "IMEI1",
      dataIndex: "imei1",
      key: "imei1",
    },
    {
      title: "Model",
      dataIndex: "deviceModel",
      key: "deviceModel",
    },
    {
      title: "Customer Name",
      dataIndex: "customerName",
      key: "customerName",
    },
    {
      title: "Customer Phone",
      dataIndex: "customerPhone",
      key: "customerPhone",
    },
    {
      title: "Registered At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date: string) => formatDate(date),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string, record: SellerDeviceListResponse) => {
        let color = "default";

        if (status === "ACTIVE") {
          color = "success";
        } else if (status === "PENDING") {
          color = "warning";
        } else if (status === "LOCKED") {
          color = "error";
        }

        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          {hasPermission(PERMISSIONS.VIEW_DEVICE.name) && (
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() =>
                navigate(PERMISSIONS.VIEW_DEVICE.route, {
                  state: { id: record.id },
                })
              }
            >
              View
            </Button>
          )}
        </Space>
      ),
    },
  ];

  if (!seller && !loading) {
    return (
      <Card>
        <Typography.Text>Seller not found</Typography.Text>
      </Card>
    );
  }

  return (
    <Card
      title={
        <Space>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(-1)}
            type="link"
          />
          <Title level={4} style={{ margin: 0 }}>
            Seller Management
          </Title>
        </Space>
      }
      loading={loading}
    >
      {seller && (
        <>
          <Descriptions
            bordered
            column={{ xxl: 4, xl: 3, lg: 3, md: 2, sm: 1, xs: 1 }}
          >
            <Descriptions.Item label="Name">{seller.name}</Descriptions.Item>
            <Descriptions.Item label="Organization">
              {seller.organization}
            </Descriptions.Item>
            <Descriptions.Item label="Phone">{seller.phone}</Descriptions.Item>
            <Descriptions.Item label="Email">{seller.email}</Descriptions.Item>
            <Descriptions.Item label="Token Balance">
              {(seller.tokenBalance || 0).toLocaleString()}
            </Descriptions.Item>
            <Descriptions.Item label="Designation">
              {seller.designation}
            </Descriptions.Item>
            <Descriptions.Item label="Seller ID">
              {seller.sellerId}
            </Descriptions.Item>
            {hasPermission(PERMISSIONS.TOGGLE_UNINSTALL_CONFIRMATION.name) && (
              <Descriptions.Item label="Uninstall Confirmation Required">
                <Switch
                  checked={seller.uninstallConfirmationRequired}
                  onChange={handleToggleUninstallConfirmation}
                  disabled={toggleLoading}
                  loading={toggleLoading}
                />
              </Descriptions.Item>
            )}
            {hasPermission(PERMISSIONS.MANAGE_SELLERS.name) && (
              <Descriptions.Item label="Due Payment Notification">
                <Switch
                  checked={seller.duePaymentNotificationEnabled ?? true}
                  onChange={handleToggleDuePaymentNotification}
                  disabled={dueNotifToggleLoading}
                  loading={dueNotifToggleLoading}
                />
              </Descriptions.Item>
            )}
          </Descriptions>

          {hasPermission(PERMISSIONS.MANAGE_SELLERS.name) && (() => {
            const due = seller.totalDue || 0;
            const hasDue = due > 0;
            const payAmountNum = Number(payAmount);
            const isValidAmount = payAmountNum > 0;
            return (
              <>
                <Divider orientation="left">
                  <Space>
                    <DollarCircleOutlined />
                    Outstanding Dues
                  </Space>
                </Divider>
                <Card
                  size="small"
                  style={{
                    marginBottom: 24,
                    borderColor: hasDue ? "#ffccc7" : "#b7eb8f",
                    background: hasDue ? "#fff2f0" : "#f6ffed",
                  }}
                >
                  <Row gutter={[24, 16]} align="middle">
                    <Col xs={24} md={7}>
                      <Statistic
                        title={
                          <Space size={4}>
                            {hasDue ? (
                              <WarningOutlined style={{ color: "#cf1322" }} />
                            ) : (
                              <CheckCircleOutlined style={{ color: "#389e0d" }} />
                            )}
                            <span>
                              {hasDue ? "Total Outstanding" : "All Clear"}
                            </span>
                          </Space>
                        }
                        value={due}
                        precision={2}
                        prefix="৳"
                        valueStyle={{
                          color: hasDue ? "#cf1322" : "#389e0d",
                          fontSize: 28,
                          fontWeight: 600,
                        }}
                      />
                    </Col>
                    <Col xs={24} md={17}>
                      {hasDue ? (
                        <Row gutter={[8, 8]}>
                          <Col xs={24} md={12}>
                            <InputNumber
                              min={0}
                              step={1}
                              placeholder="Amount"
                              value={payAmount === "" ? null : payAmountNum}
                              onChange={(val) =>
                                setPayAmount(val == null ? "" : String(val))
                              }
                              disabled={payLoading}
                              onPressEnter={handlePaySellerDues}
                              prefix="৳"
                              style={{ width: "100%" }}
                              size="large"
                            />
                          </Col>
                          <Col xs={12} md={5}>
                            <Button
                              size="large"
                              onClick={() => setPayAmount(String(due))}
                              disabled={payLoading}
                              block
                            >
                              Pay Full
                            </Button>
                          </Col>
                          <Col xs={12} md={7}>
                            <Button
                              type="primary"
                              size="large"
                              icon={<DollarCircleOutlined />}
                              onClick={handlePaySellerDues}
                              loading={payLoading}
                              disabled={!isValidAmount}
                              block
                            >
                              Pay
                            </Button>
                          </Col>
                        </Row>
                      ) : (
                        <Alert
                          type="success"
                          showIcon
                          message="No outstanding dues for this seller."
                          style={{ marginBottom: 0 }}
                        />
                      )}
                      {hasDue && isValidAmount && payAmountNum > due && (
                        <Alert
                          type="warning"
                          showIcon
                          style={{ marginTop: 12 }}
                          message={`Overpayment of BDT ${(
                            payAmountNum - due
                          ).toLocaleString()} will be recorded as advance credit.`}
                        />
                      )}
                    </Col>
                  </Row>
                  {hasDue && (
                    <Typography.Text
                      type="secondary"
                      style={{ fontSize: 12, display: "block", marginTop: 12 }}
                    >
                      Payments are applied to the most recent unpaid sale first,
                      cascading backwards until the amount is exhausted.
                    </Typography.Text>
                  )}
                </Card>
              </>
            );
          })()}

          <Divider orientation="left">Devices</Divider>

          <Form form={form} layout="vertical" onFinish={handleSearch}>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col xs={24} md={8}>
                <Form.Item label="Show Blocked Devices">
                  <Switch
                    checked={blockedDevices}
                    onChange={(checked) => {
                      setBlockedDevices(checked);
                      handleSearch();
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Date Range">
                  <RangePicker
                    onChange={handleDateRangeChange}
                    style={{ width: "100%" }}
                    placeholder={["Start Date", "End Date"]}
                    format="YYYY-MM-DD"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Search by IMEI1 or Phone Number">
                  <Input
                    placeholder="Enter IMEI1 or Phone Number"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    prefix={<SearchOutlined />}
                    allowClear
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col span={24} style={{ textAlign: "right", marginBottom: 16 }}>
                <Space>
                  <Button
                    type="primary"
                    onClick={handleSearch}
                    icon={<SearchOutlined />}
                  >
                    {/* Search */}
                  </Button>
                  {hasPermission(PERMISSIONS.MANAGE_DEVICE.name) &&
                    selectedDeviceIds.length > 0 && (
                      <Button
                        type="primary"
                        danger
                        icon={<KeyOutlined />}
                        loading={unlockLoading}
                        onClick={handleUnlockSelectedDevices}
                      >
                        Unlock Selected ({`${selectedDeviceIds.length}`})
                      </Button>
                    )}
                </Space>
              </Col>
            </Row>
          </Form>

          <Table
            columns={deviceColumns}
            dataSource={devices}
            rowKey="id"
            loading={deviceLoading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} devices`,
              onChange: handleTableChange,
              pageSizeOptions: ["10", "20", "50", "100"],
            }}
          />
        </>
      )}
    </Card>
  );
};

export default ManageSellerPage;
